import { useState, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useGalleryImages, type GalleryImage } from '@/hooks/use-supabase-data'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Upload,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ImageUploadItem {
  id: string
  file: File
  preview: string
  uploading: boolean
  uploaded: boolean
  imageUrl?: string
  error?: string
}

export function GalleryManager() {
  const { images, loading, error, refetch } = useGalleryImages(undefined, true) // Include inactive images in admin
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploadQueue, setUploadQueue] = useState<ImageUploadItem[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const newItems: ImageUploadItem[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(2)}`,
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
    }))

    setUploadQueue((prev) => [...prev, ...newItems])
    setIsDialogOpen(true)
    
    // Reset file input
    if (e.target) {
      e.target.value = ''
    }
  }

  const handleRemoveFromQueue = (id: string) => {
    setUploadQueue((prev) => {
      const item = prev.find((i) => i.id === id)
      if (item && !item.uploaded) {
        URL.revokeObjectURL(item.preview)
      }
      return prev.filter((i) => i.id !== id)
    })
  }

  const uploadImage = async (item: ImageUploadItem): Promise<string | null> => {
    try {
      // Upload to Supabase Storage
      const fileExt = item.file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `gallery/${fileName}`

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('village-portal-images')
        .upload(filePath, item.file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('village-portal-images')
        .getPublicUrl(filePath)

      return urlData.publicUrl
    } catch (err: any) {
      throw new Error(err.message || 'Failed to upload image')
    }
  }

  const handleUploadAll = async () => {
    if (uploadQueue.length === 0) return

    setStatusMessage('')
    setStatusType('')

    // Upload images one by one
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i]
      
      // Skip if already uploaded
      if (item.uploaded) continue

      // Mark as uploading
      setUploadQueue((prev) =>
        prev.map((img) =>
          img.id === item.id ? { ...img, uploading: true, error: undefined } : img
        )
      )

      try {
        const imageUrl = await uploadImage(item)
        if (!imageUrl) throw new Error('Failed to get image URL')

        // Save to database
        const { error: dbError } = await supabase.from('gallery_images').insert({
          image_url: imageUrl,
          title: null,
          description: null,
          category: selectedCategory.trim() || null,
          display_order: 0,
          is_active: true,
          created_at: new Date().toISOString(),
        })

        if (dbError) throw dbError

        // Mark as uploaded
        setUploadQueue((prev) =>
          prev.map((img) =>
            img.id === item.id
              ? { ...img, uploading: false, uploaded: true, imageUrl }
              : img
          )
        )
      } catch (err: any) {
        setUploadQueue((prev) =>
          prev.map((img) =>
            img.id === item.id
              ? { ...img, uploading: false, error: err.message || 'Upload failed' }
              : img
          )
        )
      }
    }

    // Check if all uploaded successfully
    const allUploaded = uploadQueue.every((item) => item.uploaded || item.error)
    if (allUploaded) {
      const successCount = uploadQueue.filter((item) => item.uploaded).length
      if (successCount > 0) {
        setStatusType('success')
        setStatusMessage(`${successCount} image(s) uploaded successfully.`)
        await refetch()
        
        // Clear queue after a delay
        setTimeout(() => {
          setUploadQueue([])
          setIsDialogOpen(false)
        }, 2000)
      }
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('gallery_images').delete().eq('id', id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Image deleted successfully.')
      await refetch()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage('Failed to delete image.')
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-500 bg-clip-text text-transparent">
            Gallery Management
          </h2>
          <p className="text-slate-600 text-lg">Upload and manage gallery images</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Images
            </Button>
          </div>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : statusType === 'error'
                ? undefined
                : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {/* Images Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Gallery Images ({images.length})
          </CardTitle>
          <CardDescription>Manage your gallery images</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mr-3" />
              Loading images...
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-10">
              <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No images found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Image</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Uploaded Date</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {images.map((image) => (
                  <TableRow key={image.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0">
                          <img
                            src={image.image_url}
                            alt="Gallery image"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">
                        {image.category || 'Uncategorized'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(image.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete image?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove this image from the gallery.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                              onClick={() => handleDelete(image.id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Queue Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Images</DialogTitle>
            <DialogDescription>
              {uploadQueue.length} image(s) selected. Images will be uploaded one by one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category (optional)</Label>
              <Input
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                placeholder="e.g., Events, Village Life, Festivals, etc."
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                All selected images will be uploaded to this category. Leave empty for uncategorized.
              </p>
            </div>
            {uploadQueue.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadQueue.map((item) => (
                  <div key={item.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-border relative">
                      <img
                        src={item.preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      {item.uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <RefreshCw className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                      {item.uploaded && (
                        <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        </div>
                      )}
                      {item.error && (
                        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                          <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                      )}
                      {!item.uploading && !item.uploaded && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                          onClick={() => handleRemoveFromQueue(item.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {item.error && (
                      <p className="text-xs text-red-600 mt-1 truncate" title={item.error}>
                        {item.error}
                      </p>
                    )}
                    {item.uploaded && (
                      <p className="text-xs text-emerald-600 mt-1">Uploaded</p>
                    )}
                    {item.uploading && (
                      <p className="text-xs text-blue-600 mt-1">Uploading...</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // Clean up preview URLs
                  uploadQueue.forEach((item) => {
                    if (!item.uploaded) {
                      URL.revokeObjectURL(item.preview)
                    }
                  })
                  setUploadQueue([])
                  setSelectedCategory('')
                  setIsDialogOpen(false)
                }}
              >
                {uploadQueue.some((item) => item.uploaded) ? 'Close' : 'Cancel'}
              </Button>
              {uploadQueue.length > 0 && !uploadQueue.every((item) => item.uploaded) && (
                <Button
                  onClick={handleUploadAll}
                  disabled={uploadQueue.some((item) => item.uploading)}
                  className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadQueue.some((item) => item.uploading)
                    ? 'Uploading...'
                    : `Upload ${uploadQueue.filter((item) => !item.uploaded).length} Image(s)`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
